import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './FormularioReserva.css';
import { guardarReserva } from '../data/reservas';
import { enviarCorreoConfirmacion } from '../services/emailService';
import Carousel from './Carousel';
import Modal from './Modal';
import { formatearFecha, formatearPrecio } from '../utils/dateUtils';
import { obtenerPrecioPorOcupacion, obtenerPrecioPorOcupacionYFecha } from '../services/preciosService';

const FormularioReserva = ({ 
  habitaciones, 
  fechaEntrada, 
  fechaSalida, 
  precioTotal, 
  duracionEstancia, 
  distribucionPersonas,
  onCancelar, 
  onReservaExitosa 
}) => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: ''
  });

  const [errors, setErrors] = useState({
    nombre: '',
    email: '',
    telefono: ''
  });

  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [habitacionSeleccionada, setHabitacionSeleccionada] = useState(null);
  const [codigoReserva, setCodigoReserva] = useState(null);

  // Efecto para inicializar datos cuando las props cambian
  useEffect(() => {
    // No es necesario resetear estos datos aquí
    // Ya están inicializados correctamente en el estado inicial del componente
  }, [habitaciones, distribucionPersonas]);

  // Estado para almacenar los precios por temporada de cada habitación
  const [preciosPorTemporada, setPreciosPorTemporada] = useState({});

  // Efecto para cargar los precios por temporada cuando cambien las fechas
  useEffect(() => {
    const cargarPreciosPorTemporada = async () => {
      console.log('FormularioReserva - Cargando precios por temporada para fecha:', fechaEntrada);
      const precios = {};
      
      for (const habitacion of habitaciones) {
        precios[habitacion.id] = {};
        
        for (let ocupacion = 1; ocupacion <= (habitacion.capacity || habitacion.capacidad || 1); ocupacion++) {
          console.log(`Consultando precio para habitación ${habitacion.id}, ocupación ${ocupacion}`);
          // Obtener precio por temporada para la fecha de entrada
          const precio = await obtenerPrecioPorOcupacionYFecha(
            habitacion,
            ocupacion,
            fechaEntrada
          );
          
          precios[habitacion.id][ocupacion] = precio;
          console.log(`Precio obtenido: ${precio}`);
        }
      }
      
      console.log('Precios por temporada cargados:', precios);
      setPreciosPorTemporada(precios);
    };
    
    cargarPreciosPorTemporada();
  }, [habitaciones, fechaEntrada]);

  const validateNombre = (nombre) => {
    if (!nombre.trim()) {
      return 'El nombre es requerido';
    }
    if (nombre.length < 3) {
      return 'El nombre debe tener al menos 3 caracteres';
    }
    if (nombre.length > 50) {
      return 'El nombre no puede exceder los 50 caracteres';
    }
    return '';
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return 'El email es requerido';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Por favor ingrese un correo electrónico válido';
    }
    return '';
  };

  const validateTelefono = (telefono) => {
    if (!telefono.trim()) {
      return 'El teléfono es requerido';
    }
    const telefonoRegex = /^[0-9+\-\s()]{8,15}$/;
    if (!telefonoRegex.test(telefono)) {
      return 'Por favor ingrese un número de teléfono válido';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Validar el campo que cambió
    let errorMessage = '';
    switch (name) {
      case 'nombre':
        errorMessage = validateNombre(value);
        break;
      case 'email':
        errorMessage = validateEmail(value);
        break;
      case 'telefono':
        errorMessage = validateTelefono(value);
        break;
    }
    
    setErrors(prev => ({
      ...prev,
      [name]: errorMessage
    }));
  };

  const validateForm = () => {
    const newErrors = {
      nombre: validateNombre(formData.nombre),
      email: validateEmail(formData.email),
      telefono: validateTelefono(formData.telefono)
    };
    
    setErrors(newErrors);
    
    return !Object.values(newErrors).some(error => error !== '');
  };

  // Función para obtener el precio según la ocupación, considerando temporadas
  const obtenerPrecioHabitacion = (habitacion, ocupacion) => {
    // Convertir ocupación a número para asegurarnos de que las comparaciones sean correctas
    const ocupacionNum = parseInt(ocupacion) || 1;
    
    // Si la ocupación es mayor a la capacidad, usar la capacidad máxima
    const ocupacionAjustada = Math.min(ocupacionNum, habitacion.capacity || habitacion.capacidad || 1);
    
    // Primero, intentar obtener el precio de temporada
    if (
      preciosPorTemporada[habitacion.id] && 
      preciosPorTemporada[habitacion.id][ocupacionAjustada]
    ) {
      const precioTemporada = parseInt(preciosPorTemporada[habitacion.id][ocupacionAjustada]);
      console.log(`Usando precio de temporada para habitación ${habitacion.id}: ${precioTemporada}`);
      return precioTemporada;
    }
    
    // Si no hay precio por temporada, usar la función normal
    return obtenerPrecioPorOcupacion(habitacion, ocupacionAjustada);
  };

  const verDetallesHabitacion = (habitacion) => {
    setHabitacionSeleccionada(habitacion);
  };

  const cerrarDetallesHabitacion = () => {
    setHabitacionSeleccionada(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validar todo el formulario antes de enviar
    if (!validateForm()) {
      return;
    }
    
    setEnviando(true);

    try {
      // Generar un único código de reserva para todas las habitaciones
      const codigoUnico = Math.floor(100000 + Math.random() * 900000).toString();
      
      // Crear una reserva para cada habitación con su distribución de personas
      const reservasPromises = habitaciones.map(async habitacion => {
        const personasEnHabitacion = distribucionPersonas[habitacion.id] || 1;
        const precioPorNoche = obtenerPrecioHabitacion(habitacion, personasEnHabitacion);
        const reservaData = {
          habitacionId: habitacion.id,
          fechaEntrada,
          fechaSalida,
          ...formData,
          personasEnHabitacion,
          precioHabitacion: precioPorNoche,
          duracionEstancia,
          precioTotal: precioPorNoche * duracionEstancia,
          codigoReserva: codigoUnico  // Usar el mismo código para todas
        };
        
        // Guardar la reserva en Firebase con el código preestablecido
        return await guardarReserva(reservaData);
      });

      // Guardar cada reserva en Firebase
      const resultadosReservas = await Promise.all(reservasPromises);
      
      // Enviar correo de confirmación
      const reservaCompleta = {
        ...formData,
        fechaEntrada,
        fechaSalida,
        habitaciones: habitaciones.map(h => ({
          ...h,
          nombre: h.nombre || `Habitación ${h.id}`, // Asegurar que nombre siempre tenga un valor
          precio: obtenerPrecioHabitacion(h, distribucionPersonas[h.id] || 1) // Asegurar que cada habitación tiene su precio de temporada
        })),
        distribucionPersonas,
        duracionEstancia,
        precioTotal: calcularPrecioTotal(), // Usar la función que calcula el precio total con precios de temporada
        codigoReserva: codigoUnico
      };
      
      const correoEnviado = await enviarCorreoConfirmacion(reservaCompleta, true);
      
      if (!correoEnviado) {
        console.warn('No se pudo enviar el correo de confirmación');
      }
      
      // Notificar que la reserva fue exitosa con el código
      onReservaExitosa(codigoUnico);
    } catch (error) {
      console.error('Error al guardar la reserva:', error);
      setError('Error al guardar la reserva. Por favor, intente nuevamente.');
    } finally {
      setEnviando(false);
    }
  };

  // Función para calcular el precio total
  const calcularPrecioTotal = () => {
    const total = habitaciones.reduce((sum, habitacion) => {
      const precioHabitacion = obtenerPrecioHabitacion(habitacion, distribucionPersonas[habitacion.id] || 1);
      return sum + precioHabitacion * duracionEstancia;
    }, 0);
    
    return total;
  };

  return (
    <div className="formulario-reserva">
      {habitacionSeleccionada && ReactDOM.createPortal(
        <div className="modal-detalle-habitacion">
          <div className="modal-contenido">
            <button className="cerrar-modal" onClick={cerrarDetallesHabitacion}>×</button>
            <h3>{habitacionSeleccionada.nombre}</h3>
            <div className="detalle-carousel">
              <Carousel 
                imagenes={habitacionSeleccionada.imagenes || [habitacionSeleccionada.imagen]} 
                altura="300px"
              />
            </div>
            <div className="detalle-info">
              <p>{habitacionSeleccionada.descripcion}</p>
              <div className="detalle-caracteristicas">
                <p><strong>Tipo:</strong> {habitacionSeleccionada.tipo}</p>
                <p><strong>Capacidad:</strong> {habitacionSeleccionada.capacity || habitacionSeleccionada.capacidad || 1} personas</p>
                <p><strong>Personas asignadas:</strong> {distribucionPersonas[habitacionSeleccionada.id] || 1}</p>
                <p><strong>Precio por noche:</strong> {formatearPrecio(obtenerPrecioHabitacion(habitacionSeleccionada, distribucionPersonas[habitacionSeleccionada.id] || 1))}</p>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <h3>Completar Reserva</h3>
      <div className="resumen-habitaciones">
        <h4>Habitaciones Seleccionadas:</h4>
        {habitaciones.map(habitacion => (
          <div key={habitacion.id} className="resumen-habitacion">
            <div className="resumen-habitacion-info">
              <p>{habitacion.nombre} - Capacidad: {habitacion.capacity || habitacion.capacidad || 1} personas</p>
              <p>Personas asignadas: {distribucionPersonas[habitacion.id] || 1}</p>
              <p>Precio: {formatearPrecio(obtenerPrecioHabitacion(habitacion, distribucionPersonas[habitacion.id] || 1))}/noche</p>
            </div>
            <button 
              type="button" 
              className="btn-ver-detalles"
              onClick={() => verDetallesHabitacion(habitacion)}
            >
              Ver fotos
            </button>
          </div>
        ))}
        <div className="resumen-fechas">
          <p>Entrada: {formatearFecha(fechaEntrada)}</p>
          <p>Salida: {formatearFecha(fechaSalida)}</p>
          <p>Duración: {duracionEstancia > 0 ? Math.round(duracionEstancia) : '...'} {Math.round(duracionEstancia) === 1 ? 'noche' : 'noches'}</p>
        </div>
        <div className="resumen-precio-total">
          <h4>Resumen de Precio</h4>
          <div className="detalle-precio">
            <p>Precio por noche: {formatearPrecio(calcularPrecioTotal() / duracionEstancia)}</p>
            <p>Noches: {duracionEstancia}</p>
            <p className="precio-total">Total: {formatearPrecio(calcularPrecioTotal())}</p>
            <p className="info-pago">* Los pagos se realizan el día de llegada, no se requiere pago por adelantado.</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="nombre">Nombre Completo:</label>
          <input
            type="text"
            id="nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            className={errors.nombre ? 'input-error' : ''}
            placeholder="Ingrese su nombre completo"
            maxLength="50"
          />
          {errors.nombre && <p className="error-text">{errors.nombre}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? 'input-error' : ''}
            placeholder="ejemplo@correo.com"
          />
          {errors.email && <p className="error-text">{errors.email}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="telefono">Teléfono:</label>
          <input
            type="tel"
            id="telefono"
            name="telefono"
            value={formData.telefono}
            onChange={handleChange}
            className={errors.telefono ? 'input-error' : ''}
            placeholder="+56 9 1234 5678"
            maxLength="15"
          />
          {errors.telefono && <p className="error-text">{errors.telefono}</p>}
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="botones-container">
          <button 
            type="submit" 
            className="btn-reservar" 
            disabled={enviando || Object.values(errors).some(error => error !== '')}
          >
            {enviando ? 'Procesando...' : 'Confirmar Reserva'}
          </button>
          <button 
            type="button" 
            onClick={() => onCancelar()} 
            className="btn-cancelar" 
            disabled={enviando}
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormularioReserva; 